import * as React from 'react';
import { Box, Link, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { fetchMainSubscription } from '@pec/aion-ui-core/slices/mainSubscription';
import { fetchOrganizationMetadata } from '../../slices/organizationMetadata';
import { fetchSubscriptions } from '../../slices/subscriptionsV2';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';
import { NavLink, useParams } from 'react-router-dom';
import { OrganizationMetadataType } from '../../interfaces/organization';
import { SubscriptionCard } from '../../components/Membership/SubscriptionCard';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: 24,
      lineHeight: '24px',
      letterSpacing: 0.18,
      color: 'var(--text-black)',
      marginBottom: theme.spacing(0.5)
    },
    subtitle: {
      fontSize: 14,
      lineHeight: '20px',
      letterSpacing: 0.25,
      color: '#000',
      marginBottom: theme.spacing(3)
    },
    link: {
      color: 'var(--primary-blue-six)',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  })
);

type RouteParams = {
  organizationId: string;
};

export const MembershipPage: React.FC = () => {
  const { organizationId } = useParams<RouteParams>();
  const classes = useStyles();
  const { t, i18n } = useTranslation('subscriber');
  const dispatch = useAppDispatch();
  const { subscriptions, isFetching: isFetchingSubscriptions } = useTypedSelector(state => state.subscriptionsV2);
  const { mainSubscription, isFetching: isFetchingMainSubscription } = useTypedSelector(
    state => state.mainSubscription
  );
  const { isRenewalAvailable, isUpgradeAvailable } = mainSubscription;
  const { organizationInfo } = useTypedSelector(state => state.organizationInfo);
  const { organizationMetadata, isFetching: isFetchingOrganizationMetadata } = useTypedSelector(
    state => state.organizationMetadata
  );
  const assessmentType =
    organizationMetadata?.organizationMetadatas.find(
      metadata => metadata.typeId === OrganizationMetadataType.AssessmentType
    )?.value ?? null;
  const isLoading = isFetchingSubscriptions || isFetchingMainSubscription || isFetchingOrganizationMetadata;
  const isUsingEnglishUS = i18n.language === 'en-US';

  const title = isUsingEnglishUS ? t('subscription.title', 'Subscription') : t('membership.title', 'Membership');
  const mainWord = isUsingEnglishUS
    ? t('subscription.mainWord', 'subscription')
    : t('membership.mainWord', 'membership');

  React.useEffect(() => {
    if (organizationId && (isRenewalAvailable || isUpgradeAvailable)) {
      dispatch(fetchOrganizationInfo(organizationId));
    }
  }, [dispatch, organizationId, isRenewalAvailable, isUpgradeAvailable]);

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchSubscriptions(organizationId));
      dispatch(fetchMainSubscription(organizationId));
      dispatch(fetchOrganizationMetadata(organizationId));
    }
  }, [dispatch, organizationId]);

  return (
    <LoadingOverlay isLoading={isLoading}>
      <Box paddingX={3} paddingY={4}>
        <Typography variant="h5" classes={{ root: classes.title }}>
          {title}
        </Typography>
        <Typography variant="subtitle1" classes={{ root: classes.subtitle }}>
          <Trans i18nKey="membership.subtitle">
            <Link color="primary" href="https://veriforce.com/company/contact-us">
              Contact us
            </Link>{' '}
            for questions about {mainWord} and billing.{' '}
            <NavLink to={`/${organizationId}#products`} className={classes.link}>
              Click here
            </NavLink>{' '}
            to learn about other Veriforce/CHAS products.
            <br />
            Note that any certification status/progress is not shown below and should instead be referenced on the
            Company Dashboard.
          </Trans>
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {subscriptions?.map(subscription => (
            <SubscriptionCard
              key={subscription.id}
              subscription={subscription}
              mainSubscription={mainSubscription}
              assessmentType={assessmentType}
              organizationInfo={organizationInfo}
            />
          ))}
        </Box>
      </Box>
    </LoadingOverlay>
  );
};
